import React, { useState, useEffect, useImperativeHandle, forwardRef, ForwardedRef } from 'react';
import FormStep from '../../../../components/form/public/step/FormStep';
import { Form } from 'react-bootstrap';
import { Formik } from 'formik';
import { buildSchema, buildValues } from '../../../../components/form/public/utilities/FormikBuilder';
import { StepModel } from '../../../../models/form/step';
import RulesHandler, {
  addSection,
  removeSection,
  RulesHandlerLogic,
} from '../../../../components/form/public/utilities/RulesHandler';
import { initializeSteps } from '../../../../components/form/public/utilities/FormUtilities';
import _ from 'lodash';
import { BuildedSchema, BuildedValues, DataFormModel } from '../../../../components/form/public/utilities/FormModels';
import { FormattedMessage, useIntl } from 'react-intl';
import { noop } from '../../../../utilities/FunctionUtilities';
import { getText } from '../../../../utilities/CultureUtilities';

export interface Props {
  form: any;
  readOnlyMode?: boolean;
  data?: DataFormModel;
  formikRef?: any;
  stepsStateProp?: any;
}

export interface SubmissionStepsRef {
  cancelEdit: () => void;
}

function SubmissionSteps(
  { form, readOnlyMode, data, formikRef, stepsStateProp }: Props,
  ref: ForwardedRef<SubmissionStepsRef>,
) {
  const [noFields, setNoFields] = useState<boolean>(false);
  const [openedSections, setOpenedSections] = useState<string[]>([]);
  const [stepsState, setStepsState] = useState<StepModel[] | undefined>(undefined);
  const [formikSchema, setFormikSchema] = useState<BuildedSchema | undefined>(undefined);
  const [formikValues, setFormikValues] = useState<BuildedValues | undefined>(undefined);
  const parsedFormContent = JSON.parse(form.content);
  const intl = useIntl();

  useImperativeHandle(ref, () => ({
    cancelEdit: () => {
      const initializedSteps = initializeSteps(parsedFormContent.steps, data?.fields);
      const formikSchema = buildSchema(initializedSteps, form.fieldProperties, true);
      setFormikSchema(formikSchema);
      setStepsState(initializedSteps);
      formikRef.current?.resetForm();
    },
  }));

  useEffect(() => {
    let haveFields = false;
    if (parsedFormContent.steps) {
      for (const step of parsedFormContent.steps) {
        for (const section of step.sections) {
          if (section.fields.length > 0) {
            haveFields = true;
          }
        }
      }
    }

    if (!haveFields) {
      setNoFields(true);
      return;
    }

    //Initialization
    const initializedSteps = initializeSteps(parsedFormContent.steps, data?.fields);
    const formikSchema = buildSchema(initializedSteps, form.fieldProperties, true);
    const formikValues = buildValues(initializedSteps, data?.fields, intl.locale);
    setFormikSchema(formikSchema);
    setFormikValues(formikValues);
    setStepsState(initializedSteps);
    RulesHandlerLogic(formikValues, initializedSteps, parsedFormContent.rules, (steps) =>
      steps.length > 0 ? setStepsState(steps) : noop,
    );
  }, [data]);

  useEffect(() => {
    stepsStateProp(stepsState);
  }, [stepsState]);

  const openSectionHandle = (section: string) => {
    let tempOpenedList: string[];
    if (
      !_.find(openedSections, (s) => {
        return s === section;
      })
    ) {
      tempOpenedList = [...openedSections, section];
      setOpenedSections(tempOpenedList);
    } else {
      const idToRemove = openedSections.indexOf(section);
      tempOpenedList = [...openedSections];
      tempOpenedList.splice(idToRemove, 1);
      setOpenedSections(tempOpenedList);
    }
  };

  if (noFields) {
    return (
      <div>
        <FormattedMessage id="form.submission.noFields" />{' '}
      </div>
    );
  }

  return (
    <div className="form-steps">
      {formikValues && formikSchema && stepsState && (
        <Formik
          validationSchema={formikSchema}
          initialValues={formikValues}
          innerRef={formikRef}
          onSubmit={noop}
          enableReinitialize
        >
          {({ values, setFieldValue }) => {
            return (
              <div className="d-flex">
                <div className="flex-1 me-4">
                  <Form noValidate>
                    <RulesHandler
                      steps={stepsState}
                      setSteps={(newSteps) => {
                        setStepsState(newSteps);
                        setFormikSchema(buildSchema(newSteps, form.fieldProperties, true));
                      }}
                      rules={parsedFormContent.rules}
                    />
                    {stepsState.map((step, stepIdx) => {
                      return (
                        <div className="formstep-container" key={stepIdx}>
                          {stepsState.length > 1 && (
                            <div className="step-number">
                              <p
                                className={
                                  openedSections.includes(getText(step.name, intl.locale)) ? 'closed' : 'opened'
                                }
                                onClick={() => openSectionHandle(getText(step.name, intl.locale))}
                              >
                                #{stepIdx + 1} {getText(step.name, intl.locale)}
                              </p>
                            </div>
                          )}
                          {!openedSections.includes(getText(step.name, intl.locale)) && (
                            <FormStep
                              readOnlyMode={readOnlyMode}
                              showHiddenOptions
                              step={stepsState[stepIdx]}
                              addSection={(sectionId: string) => {
                                addSection(
                                  values,
                                  sectionId,
                                  stepsState,
                                  parsedFormContent.rules,
                                  (newSteps) => {
                                    setStepsState(newSteps);
                                    setFormikSchema(buildSchema(newSteps, form.fieldProperties, true));
                                  },
                                  setFieldValue,
                                );
                              }}
                              removeSection={(sectionId: string) =>
                                removeSection(
                                  values,
                                  sectionId,
                                  stepsState,
                                  parsedFormContent.rules,
                                  (newSteps) => {
                                    setStepsState(newSteps);
                                    setFormikSchema(buildSchema(newSteps, form.fieldProperties, true));
                                  },
                                  setFieldValue,
                                )
                              }
                            />
                          )}
                        </div>
                      );
                    })}
                  </Form>
                </div>
              </div>
            );
          }}
        </Formik>
      )}
    </div>
  );
}

export default forwardRef(SubmissionSteps);
